import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Bell, Clock, AlertTriangle, DoorOpen, X, ArrowLeftRight, Megaphone, Radio, Award } from "lucide-react";

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

// Header bell + notifications dropdown. Viewable from any page without navigating away.
// `m` switches the panel to the mobile dark palette. Trigger sits on dark chrome in both placements.
export default function NotificationsBell({ m = false }) {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [unread, setUnread] = useState(0);

  const loadCount = useCallback(() => api.get("/notifications/unread-count").then((r) => setUnread(r.data.unread || 0)).catch(() => {}), []);
  const loadList = useCallback(() => api.get("/notifications").then((r) => { setNotifs(r.data.notifications || []); setUnread(r.data.unread || 0); }).catch(() => {}), []);

  useEffect(() => {
    loadCount();
    const iv = setInterval(loadCount, 120000);
    const h = () => loadCount();
    window.addEventListener("rp-notifs-read", h);
    return () => { clearInterval(iv); window.removeEventListener("rp-notifs-read", h); };
  }, [loadCount]);
  useEffect(() => { if (open) loadList(); }, [open, loadList]);

  const markAll = () => { api.post("/notifications/read", {}).then(loadList).catch(() => {}); window.dispatchEvent(new Event("rp-notifs-read")); };
  const dismiss = (e, id) => {
    e.stopPropagation();
    setNotifs((n) => n.filter((x) => x.id !== id));
    api.delete(`/notifications/${id}`).then(loadCount).catch(() => {});
    window.dispatchEvent(new Event("rp-notifs-read"));
  };
  const openNotif = (n) => {
    if (!n.read) { api.post("/notifications/read", { id: n.id }).catch(() => {}); window.dispatchEvent(new Event("rp-notifs-read")); }
    setOpen(false);
    if (n.rodeo_id) nav(`/rodeo/${n.rodeo_id}?tab=entries`);
  };

  const theme = m
    ? { panel: "bg-[#0F141B] border-white/10 text-slate-100", sub: "text-slate-400", item: "bg-white/5 border-white/10", head: "border-white/10" }
    : { panel: "bg-white border-[#E4E7EB] text-[#14181F]", sub: "text-slate-500", item: "bg-white border-[#E4E7EB]", head: "border-[#EEF0F2]" };

  const iconFor = (t) => t === "entry_open"
    ? { Icon: DoorOpen, cls: "bg-[#3B6B1E]/15 text-[#3B6B1E]" }
    : t === "rider_up"
      ? { Icon: Radio, cls: "bg-[#C8000F]/15 text-[#C8000F]" }
      : t === "rider_placed"
        ? { Icon: Award, cls: "bg-[#3B6B1E]/15 text-[#3B6B1E]" }
        : t === "clash"
          ? { Icon: ArrowLeftRight, cls: "bg-[#E68A2E]/15 text-[#E68A2E]" }
          : t === "announcement"
            ? { Icon: Megaphone, cls: "bg-[#E6B35A]/20 text-[#B4801F]" }
            : t === "deadline_change"
              ? { Icon: AlertTriangle, cls: "bg-[#E68A2E]/15 text-[#E68A2E]" }
              : { Icon: Clock, cls: "bg-[#C8000F]/15 text-[#C8000F]" };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid="notif-bell" aria-label="Notifications" className="relative text-slate-300 hover:text-white p-2 rounded-lg">
          <Bell className="w-5 h-5" />
          {unread > 0 && (
            <span data-testid="notif-bell-badge" className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-[#C8000F] text-white text-[10px] font-bold flex items-center justify-center">{unread > 9 ? "9+" : unread}</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={8} className={`w-[340px] max-w-[92vw] p-0 rounded-xl border ${theme.panel}`} data-testid="notif-dropdown">
        <div className={`flex items-center justify-between px-3 py-2.5 border-b ${theme.head}`}>
          <div className="font-display font-bold text-sm flex items-center gap-2">
            Notifications {unread > 0 && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-[#C8000F] text-white">{unread}</span>}
          </div>
          {notifs.some((n) => !n.read) && (
            <button data-testid="notif-mark-all" onClick={markAll} className="text-[11px] font-semibold text-[#C8000F] hover:underline">Mark all read</button>
          )}
        </div>
        <div className="max-h-[70vh] overflow-y-auto">
          {notifs.length === 0 ? (
            <div className={`text-sm py-10 px-4 text-center ${theme.sub}`}>No alerts yet. Star a rodeo or pick a performance and we'll remind you about entries.</div>
          ) : notifs.map((n) => {
            const { Icon, cls } = iconFor(n.type);
            return (
              <div key={n.id} data-testid={`notif-item-${n.id}`} onClick={() => openNotif(n)}
                className={`group relative flex items-start gap-2.5 px-3 py-2.5 border-b cursor-pointer transition-colors ${theme.head} ${!n.read ? (m ? "bg-white/[0.06]" : "bg-[#FDF2F3]") : ""} ${m ? "hover:bg-white/5" : "hover:bg-[#FAFBFC]"}`}>
                <span className={`mt-0.5 shrink-0 w-7 h-7 rounded-full flex items-center justify-center ${cls}`}><Icon className="w-4 h-4" /></span>
                <div className="min-w-0 flex-1 pr-5">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-sm truncate">{n.title}</span>
                    {n.priority === "high" && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-[#C8000F] text-white uppercase shrink-0">Priority</span>}
                  </div>
                  <div className={`text-xs mt-0.5 ${theme.sub}`}>{n.body}</div>
                  <div className={`text-[10px] mt-1 ${theme.sub}`}>{timeAgo(n.created_at)}</div>
                </div>
                <button data-testid={`notif-dismiss-${n.id}`} onClick={(e) => dismiss(e, n.id)} aria-label="Dismiss"
                  className={`absolute top-2 right-2 p-1 rounded ${m ? "text-slate-500 hover:text-white hover:bg-white/10" : "text-slate-400 hover:text-slate-700 hover:bg-slate-100"}`}>
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
